import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useCallback,
    useState,
} from 'react';
import { toast } from 'react-hot-toast';
import i18next from 'i18next';

import useLocalStorage from '../hooks/useLocalStorage';
import { createExport, getExportStatus, downloadExport } from '../api/apiExports';
import parseError from '../utils/parseError';

// ==========================================
// Job shape
//   id            — backend export id (assigned after POST)
//   type          — 'orders' | 'customers' | 'inventory' | 'revenue'
//   filters       — snapshot of filter state when the job was queued
//   status        — 'pending' | 'completed' | 'failed' (mirrors API)
//   started_at    — ISO timestamp set client-side
//   error         — backend error string if status='failed'
//   downloaded    — true once the user has clicked Download (or auto)
// ==========================================

const POLL_INTERVAL_MS = 5000;
const STORAGE_KEY = 'admin.exports.jobs';
const MAX_KEEP = 10; // keep at most last N jobs in the tray

const ExportsContext = createContext(null);

export const useExports = () => {
    const ctx = useContext(ExportsContext);
    if (!ctx) throw new Error('useExports must be used inside ExportsProvider');
    return ctx;
};

const isArabic = () => (i18next.language || 'en').startsWith('ar');

const TYPE_LABELS = {
    orders:    { en: 'Orders',    ar: 'الطلبات' },
    customers: { en: 'Customers', ar: 'العملاء' },
    inventory: { en: 'Inventory', ar: 'المخزون' },
    revenue:   { en: 'Revenue',   ar: 'الإيرادات' },
};

export const labelForType = (type) => {
    const ar = isArabic();
    const meta = TYPE_LABELS[type];
    if (!meta) return type;
    return ar ? meta.ar : meta.en;
};

const triggerBrowserDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const ExportsProvider = ({ children }) => {
    // Persisted across page reloads. The tray stays open across nav.
    const [jobs, setJobs] = useLocalStorage(STORAGE_KEY, []);
    const [trayOpen, setTrayOpen] = useState(false);
    const pollersRef = useRef(new Map()); // jobId → intervalId
    const downloadingRef = useRef(new Set()); // jobIds currently downloading

    // Keep a stable mutator closure so the poller doesn't capture
    // the initial jobs snapshot.
    const jobsRef = useRef(jobs);
    useEffect(() => {
        jobsRef.current = jobs;
    }, [jobs]);

    const upsertJob = useCallback((next) => {
        setJobs((prev) => {
            const others = prev.filter((j) => j.id !== next.id && (next.tempId ? j.id !== next.tempId : true));
            return [next, ...others].slice(0, MAX_KEEP);
        });
    }, [setJobs]);

    const removeJob = useCallback((jobId) => {
        setJobs((prev) => prev.filter((j) => j.id !== jobId));
    }, [setJobs]);

    const downloadJob = useCallback(async (jobId) => {
        if (downloadingRef.current.has(jobId)) return;
        downloadingRef.current.add(jobId);
        try {
            const res = await downloadExport(jobId);
            const filename = (res.headers?.['content-disposition'] || '')
                .match(/filename="?([^"]+)"?/)?.[1] || `export-${jobId}.csv`;
            triggerBrowserDownload(new Blob([res.data], { type: 'text/csv' }), filename);

            setJobs((prev) =>
                prev.map((j) => (j.id === jobId ? { ...j, downloaded: true } : j))
            );
        } catch (err) {
            toast.error(parseError(err) || 'Failed to download export');
        } finally {
            downloadingRef.current.delete(jobId);
        }
    }, [setJobs]);

    const stopPoller = useCallback((jobId) => {
        const id = pollersRef.current.get(jobId);
        if (id) {
            clearInterval(id);
            pollersRef.current.delete(jobId);
        }
    }, []);

    const startPoller = useCallback((jobId) => {
        if (!jobId) return;
        if (pollersRef.current.has(jobId)) return;

        const tick = async () => {
            try {
                const res = await getExportStatus(jobId);
                const data = res?.data || res || {};
                const status = data.status;
                if (!status) return;

                const job = jobsRef.current.find((j) => j.id === jobId);
                if (!job) {
                    stopPoller(jobId);
                    return;
                }

                if (status === 'completed') {
                    stopPoller(jobId);
                    setJobs((prev) =>
                        prev.map((j) => (j.id === jobId ? { ...j, status: 'completed' } : j))
                    );
                    toast.success(
                        isArabic()
                            ? `تم تجهيز ملف ${labelForType(job.type)}`
                            : `${labelForType(job.type)} export ready`
                    );
                    setTrayOpen(true);
                    // Auto-download once. The user can re-download from the tray.
                    if (!job.downloaded) downloadJob(jobId);
                } else if (status === 'failed') {
                    stopPoller(jobId);
                    setJobs((prev) =>
                        prev.map((j) => (j.id === jobId
                            ? { ...j, status: 'failed', error: data.error || data.message }
                            : j))
                    );
                    toast.error(
                        isArabic()
                            ? `فشل تصدير ${labelForType(job.type)}`
                            : `${labelForType(job.type)} export failed`
                    );
                    setTrayOpen(true);
                }
            } catch (err) {
                // 404 → the job no longer exists; stop polling and remove.
                if (err?.response?.status === 404) {
                    stopPoller(jobId);
                    removeJob(jobId);
                }
                // Transient errors: keep polling — Backend Sprint 14.2 spec.
            }
        };

        // Tick immediately, then every POLL_INTERVAL_MS.
        tick();
        const intervalId = setInterval(tick, POLL_INTERVAL_MS);
        pollersRef.current.set(jobId, intervalId);
    }, [setJobs, stopPoller, removeJob, downloadJob]);

    // On mount, resume any pending jobs (page reload while a job
    // was in flight).
    useEffect(() => {
        const pollers = pollersRef.current;
        jobs.forEach((job) => {
            if (job.status === 'pending' && job.id) {
                startPoller(job.id);
            }
        });
        return () => {
            pollers.forEach((id) => clearInterval(id));
            pollers.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const queueExport = useCallback(async (type, filters = {}) => {
        const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const optimistic = {
            id: tempId,
            tempId,
            type,
            filters,
            status: 'pending',
            started_at: new Date().toISOString(),
        };
        upsertJob(optimistic);
        setTrayOpen(true);

        try {
            const res = await createExport(type, filters);
            const data = res?.data || res || {};
            const realId = data.job_id || data.id;
            if (!realId) {
                throw new Error('Backend did not return job_id');
            }
            // Replace optimistic row with the real id.
            setJobs((prev) =>
                prev
                    .filter((j) => j.id !== tempId)
                    .concat({ ...optimistic, id: realId, tempId: undefined })
                    .slice(0, MAX_KEEP)
            );

            toast.success(
                isArabic()
                    ? 'تم وضع التصدير في الطابور — سنُعلمك عند الانتهاء.'
                    : "Export queued — we'll let you know when it's ready."
            );

            startPoller(realId);
            return realId;
        } catch (err) {
            // Drop the optimistic row; surface the error inline.
            setJobs((prev) => prev.filter((j) => j.id !== tempId));
            toast.error(parseError(err) || 'Failed to queue export');
            throw err;
        }
    }, [upsertJob, setJobs, startPoller]);

    const clearCompleted = useCallback(() => {
        setJobs((prev) => prev.filter((j) => j.status === 'pending'));
    }, [setJobs]);

    const value = {
        jobs,
        trayOpen,
        setTrayOpen,
        queueExport,
        downloadJob,
        removeJob,
        clearCompleted,
    };

    return (
        <ExportsContext.Provider value={value}>
            {children}
        </ExportsContext.Provider>
    );
};

export default ExportsContext;
